import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { FaWhatsapp } from 'react-icons/fa';
import Icon from './AppIcon';
import { WHATSAPP_DISPLAY, WA_MESSAGES, openWhatsApp } from '../config/site';

/**
 * Floating WhatsApp desk — a 3D glass chat panel that opens from a
 * glossy, tilt-reactive orb. Used on every page.
 */

const QUICK_REPLIES = [
  { id: 'id', label: '🎯 Get my Gaming ID', message: WA_MESSAGES.newId },
  { id: 'login', label: '🔐 Nitin online book login', message: WA_MESSAGES.login },
  { id: 'app', label: '📲 App download link', message: WA_MESSAGES.appDownload },
  { id: 'cash', label: '⚡ Deposit & withdrawal', message: WA_MESSAGES.withdrawal },
  { id: 'vip', label: '👑 VIP limits & cashback', message: WA_MESSAGES.vip },
];

const CHAT_SCRIPT = [
  {
    id: 1,
    text: 'Namaste 👋 You are chatting with the official Nitin Online Book VIP desk.',
    delay: 400,
  },
  {
    id: 2,
    text: 'I can open your Nitin Online Book ID in 2 minutes — sign up, login help or app download, whatever you need.',
    delay: 1500,
  },
  {
    id: 3,
    text: 'Withdrawals land in about 5 minutes, 24×7. Tap a topic below and I will reply on WhatsApp instantly. 💛',
    delay: 2900,
  },
];

const TEASERS = [
  'Your gaming ID is ready — shall I activate it? 👑',
  '₹15,000 welcome bonus is live for new sign ups 🎁',
  'Need the Nitin online book app download link? 📲',
  'Withdrawal stuck? We fix it in under 5 minutes ⚡',
];

const WhatsAppFloat = () => {
  const [mounted, setMounted] = useState(false);
  const [open, setOpen] = useState(false);
  const [visibleBubbles, setVisibleBubbles] = useState(0);
  const [typing, setTyping] = useState(false);
  const [teaser, setTeaser] = useState(null);
  const [unread, setUnread] = useState(1);
  const [draft, setDraft] = useState('');
  const [tilt, setTilt] = useState({ x: 0, y: 0 });
  const orbRef = useRef(null);
  const timersRef = useRef([]);

  /* Reveal the orb shortly after load. */
  useEffect(() => {
    const t = setTimeout(() => setMounted(true), 1200);
    return () => clearTimeout(t);
  }, []);

  /* Rotating teaser bubble while the panel is closed. */
  useEffect(() => {
    if (open) return undefined;
    let index = 0;
    const show = () => {
      setTeaser(TEASERS[index % TEASERS.length]);
      index += 1;
      setUnread((u) => Math.min(u + 1, 9));
      setTimeout(() => setTeaser(null), 6000);
    };
    const first = setTimeout(show, 6000);
    const loop = setInterval(show, 22000);
    return () => {
      clearTimeout(first);
      clearInterval(loop);
    };
  }, [open]);

  /* Play the scripted conversation whenever the panel opens. */
  useEffect(() => {
    timersRef.current.forEach(clearTimeout);
    timersRef.current = [];

    if (!open) {
      setVisibleBubbles(0);
      setTyping(false);
      return undefined;
    }

    setUnread(0);
    setTeaser(null);
    CHAT_SCRIPT.forEach((line, i) => {
      timersRef.current.push(setTimeout(() => setTyping(true), Math.max(0, line.delay - 700)));
      timersRef.current.push(
        setTimeout(() => {
          setTyping(i < CHAT_SCRIPT.length - 1);
          setVisibleBubbles(i + 1);
        }, line.delay)
      );
    });
    timersRef.current.push(
      setTimeout(() => setTyping(false), CHAT_SCRIPT[CHAT_SCRIPT.length - 1].delay + 300)
    );

    return () => timersRef.current.forEach(clearTimeout);
  }, [open]);

  /* Close on Escape. */
  useEffect(() => {
    const onKey = (e) => e.key === 'Escape' && setOpen(false);
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  const handleOrbMove = useCallback((e) => {
    const rect = orbRef.current?.getBoundingClientRect();
    if (!rect) return;
    const px = (e.clientX - rect.left) / rect.width - 0.5;
    const py = (e.clientY - rect.top) / rect.height - 0.5;
    setTilt({ x: -py * 26, y: px * 26 });
  }, []);

  const send = useCallback((message) => {
    openWhatsApp(message);
    setOpen(false);
    setDraft('');
  }, []);

  const orbStyle = useMemo(
    () => ({ transform: `rotateX(${tilt.x}deg) rotateY(${tilt.y}deg)` }),
    [tilt]
  );

  if (!mounted) return null;

  const panel = (
    <div
      className="relative w-[22.5rem] max-w-[calc(100vw-2rem)] origin-bottom-right animate-pop-3d"
      style={{ transformStyle: 'preserve-3d' }}
    >
      {/* Depth plates stacked behind the panel */}
      <div className="absolute -inset-x-3 -bottom-3 top-6 rounded-[28px] bg-wa-green/25 blur-xl" aria-hidden="true" />
      <div className="absolute inset-x-2 -bottom-2 top-4 rounded-[28px] border border-white/10 bg-white/5" aria-hidden="true" />

      <div className="relative overflow-hidden rounded-[26px] border border-white/12 bg-[#0B0F14] shadow-[0_40px_90px_-30px_rgba(0,0,0,0.95),0_0_60px_-24px_rgba(37,211,102,0.6)]">
        {/* Header */}
        <div className="relative overflow-hidden px-5 pb-5 pt-4">
          <div className="absolute inset-0 bg-gradient-to-br from-[#128C4B] via-[#1FB65C] to-[#0A6634]" aria-hidden="true" />
          <div className="absolute -right-10 -top-14 h-40 w-40 rounded-full bg-white/20 blur-2xl" aria-hidden="true" />
          <div className="pointer-events-none absolute inset-x-0 top-0 h-px bg-white/50" aria-hidden="true" />

          <div className="relative flex items-start gap-3">
            <div className="relative shrink-0">
              <div className="absolute inset-0 rounded-full bg-white/40 blur-md" aria-hidden="true" />
              <div className="relative flex h-12 w-12 items-center justify-center rounded-full border-2 border-white/70 bg-gradient-to-br from-[#FFF0B8] to-[#C4901A] shadow-[0_10px_20px_-8px_rgba(0,0,0,0.8)]">
                <Icon name="Crown" size={22} color="#0B0B14" />
              </div>
              <span className="absolute -bottom-0.5 -right-0.5 flex h-4 w-4 items-center justify-center rounded-full border-2 border-[#0F7A45] bg-[#7BF7AC]">
                <span className="h-1.5 w-1.5 animate-ping rounded-full bg-white" />
              </span>
            </div>

            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-1.5">
                <p className="truncate font-display text-[0.95rem] font-semibold text-white">
                  Nitin Online Book · VIP Desk
                </p>
                <Icon name="BadgeCheck" size={15} color="#FFF0B8" />
              </div>
              <p className="mt-0.5 text-[0.72rem] font-medium text-white/85">
                Online now · replies in ~2 min · 24×7
              </p>
              <p className="mt-1 inline-flex items-center gap-1 rounded-full bg-black/25 px-2 py-0.5 text-[0.68rem] text-white/90">
                <Icon name="Phone" size={11} /> {WHATSAPP_DISPLAY}
              </p>
            </div>

            <button
              onClick={() => setOpen(false)}
              aria-label="Close WhatsApp chat"
              className="rounded-full bg-black/25 p-1.5 text-white/90 transition hover:rotate-90 hover:bg-black/45"
            >
              <Icon name="X" size={16} />
            </button>
          </div>
        </div>

        {/* Conversation */}
        <div
          className="relative max-h-[16rem] space-y-2.5 overflow-y-auto px-4 py-4 no-scrollbar"
          style={{
            backgroundColor: '#080B10',
            backgroundImage:
              "radial-gradient(circle at 18% 12%, rgba(37,211,102,0.12), transparent 45%), radial-gradient(circle at 85% 80%, rgba(240,194,75,0.10), transparent 45%), url(\"/assets/images/gen/royal-pattern.svg\")",
            backgroundSize: 'auto, auto, 120px 120px',
          }}
        >
          {CHAT_SCRIPT.slice(0, visibleBubbles).map((line) => (
            <div key={line.id} className="animate-rise">
              <div className="relative max-w-[92%] rounded-2xl rounded-tl-md border border-white/10 bg-[#141C23] px-3.5 py-2.5 text-[0.82rem] leading-relaxed text-white/90 shadow-[0_10px_22px_-14px_rgba(0,0,0,0.9)]">
                {line.text}
                <span className="mt-1 block text-right text-[0.6rem] text-white/40">now</span>
              </div>
            </div>
          ))}

          {typing && (
            <div className="flex w-16 items-center justify-center gap-1 rounded-2xl rounded-tl-md border border-white/10 bg-[#141C23] px-3 py-3">
              {[0, 1, 2].map((d) => (
                <span
                  key={d}
                  className="h-1.5 w-1.5 rounded-full bg-wa-green"
                  style={{ animation: `typing-dot 1.2s ease-in-out ${d * 0.18}s infinite` }}
                />
              ))}
            </div>
          )}
        </div>

        {/* Quick replies */}
        <div className="border-t border-white/8 bg-[#0B0F14] px-4 py-3">
          <p className="mb-2 text-[0.65rem] font-semibold uppercase tracking-[0.18em] text-white/40">
            Tap a topic
          </p>
          <div className="flex flex-wrap gap-2">
            {QUICK_REPLIES.map((q) => (
              <button
                key={q.id}
                onClick={() => send(q.message)}
                className="rounded-full border border-wa-green/35 bg-wa-green/10 px-3 py-1.5 text-[0.74rem] font-medium text-white/90 shadow-[0_1px_0_rgba(255,255,255,0.12)_inset] transition hover:-translate-y-0.5 hover:border-wa-green hover:bg-wa-green/20"
              >
                {q.label}
              </button>
            ))}
          </div>
        </div>

        {/* Composer */}
        <div className="flex items-center gap-2 border-t border-white/8 bg-[#0B0F14] px-4 py-3">
          <input
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && send(draft || WA_MESSAGES.general)}
            placeholder="Type your message…"
            className="h-11 flex-1 rounded-full border border-white/10 bg-white/5 px-4 text-[0.82rem] text-white placeholder:text-white/35 focus:border-wa-green/60 focus:outline-none focus:ring-0"
          />
          <button
            onClick={() => send(draft || WA_MESSAGES.general)}
            aria-label="Send on WhatsApp"
            className="btn-3d btn-3d-wa h-11 w-11 !p-0"
          >
            <Icon name="Send" size={18} />
          </button>
        </div>

        <div className="flex items-center justify-center gap-4 border-t border-white/8 bg-black/40 px-4 py-2 text-[0.62rem] text-white/45">
          <span className="inline-flex items-center gap-1"><Icon name="ShieldCheck" size={11} /> Verified desk</span>
          <span className="inline-flex items-center gap-1"><Icon name="Zap" size={11} /> 5 min payouts</span>
          <span className="inline-flex items-center gap-1"><Icon name="Lock" size={11} /> Private chat</span>
        </div>
      </div>
    </div>
  );

  return (
    <>
      {/* ---------------- Desktop / tablet ---------------- */}
      <div className="perspective fixed bottom-6 right-6 z-floating hidden select-none flex-col items-end gap-3 md:flex">
        {open && panel}

        {!open && teaser && (
          <div className="animate-pop-3d relative mr-1 max-w-[16.5rem] rounded-2xl rounded-br-md border border-white/12 bg-[#0F141A] px-4 py-3 text-[0.8rem] leading-snug text-white/90 shadow-[0_24px_46px_-24px_rgba(0,0,0,0.95)]">
            <span className="mb-1 block text-[0.62rem] font-semibold uppercase tracking-[0.16em] text-wa-green">
              VIP Desk
            </span>
            {teaser}
            <span className="absolute -bottom-1.5 right-6 h-3 w-3 rotate-45 border-b border-r border-white/12 bg-[#0F141A]" />
          </div>
        )}

        {/* The orb */}
        <div className="perspective relative">
          <span className="pointer-events-none absolute -inset-6 rounded-full bg-wa-green/25 blur-2xl" aria-hidden="true" />
          <span className="pointer-events-none absolute inset-0 animate-ping rounded-full bg-wa-green/25" aria-hidden="true" />
          <span
            className="pointer-events-none absolute -inset-1.5 rounded-full opacity-80 animate-spin-slow"
            style={{
              background:
                'conic-gradient(from 0deg, rgba(37,211,102,0), rgba(240,194,75,0.9), rgba(37,211,102,0.9), rgba(37,211,102,0))',
              mask: 'radial-gradient(farthest-side, transparent calc(100% - 3px), #000 calc(100% - 3px))',
              WebkitMask: 'radial-gradient(farthest-side, transparent calc(100% - 3px), #000 calc(100% - 3px))',
            }}
            aria-hidden="true"
          />

          <button
            ref={orbRef}
            onClick={() => setOpen((o) => !o)}
            onMouseMove={handleOrbMove}
            onMouseLeave={() => setTilt({ x: 0, y: 0 })}
            aria-label={open ? 'Close WhatsApp chat' : 'Chat with Nitin Online Book on WhatsApp'}
            className="preserve-3d relative flex h-[4.5rem] w-[4.5rem] items-center justify-center rounded-full transition-transform duration-200 ease-royal"
            style={{
              ...orbStyle,
              background: 'radial-gradient(circle at 32% 26%, #8CFFC0 0%, #25D366 42%, #0E8A46 72%, #05502A 100%)',
              boxShadow:
                '0 1px 0 rgba(255,255,255,0.75) inset, 0 -6px 14px rgba(0,0,0,0.45) inset, 0 22px 40px -14px rgba(0,0,0,0.9), 0 0 48px -6px rgba(37,211,102,0.65)',
            }}
          >
            <span
              className="pointer-events-none absolute left-4 top-3 h-5 w-9 rounded-full bg-white/55 blur-[6px]"
              aria-hidden="true"
            />
            <FaWhatsapp
              className="relative text-white drop-shadow-[0_4px_8px_rgba(0,0,0,0.5)]"
              size={38}
              style={{ transform: 'translateZ(26px)' }}
            />

            {!open && unread > 0 && (
              <span
                className="absolute -right-1 -top-1 flex h-6 w-6 items-center justify-center rounded-full border-2 border-[#06060C] bg-victory-red text-[0.7rem] font-bold text-white shadow-[0_0_18px_rgba(255,61,113,0.8)]"
                style={{ transform: 'translateZ(34px)' }}
              >
                {unread}
              </span>
            )}
          </button>

          {/* Contact shadow on the floor */}
          <span
            className="pointer-events-none absolute -bottom-3 left-1/2 h-3 w-14 -translate-x-1/2 rounded-full bg-black/70 blur-md"
            aria-hidden="true"
          />
        </div>

        {!open && (
          <div className="rounded-full border border-white/10 bg-black/50 px-3 py-1 text-[0.65rem] font-semibold uppercase tracking-[0.16em] text-white/70 backdrop-blur">
            24×7 Live Support
          </div>
        )}
      </div>

      {/* ---------------- Mobile ---------------- */}
      <div className="fixed inset-x-0 bottom-0 z-floating md:hidden">
        {open && (
          <>
            <div
              className="fixed inset-0 bg-black/70 backdrop-blur-sm"
              onClick={() => setOpen(false)}
              aria-hidden="true"
            />
            <div className="perspective relative px-3 pb-3">{panel}</div>
          </>
        )}

        {!open && (
          <div className="relative border-t border-white/10 bg-[#0A0A12]/95 px-3 py-2.5 backdrop-blur-xl shadow-[0_-18px_40px_-24px_rgba(0,0,0,1)]">
            <div className="pointer-events-none absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-royal-gold/70 to-transparent" />
            <div className="flex items-center gap-3">
              <div className="relative shrink-0">
                <span className="absolute -inset-2 rounded-full bg-wa-green/25 blur-lg" aria-hidden="true" />
                <div
                  className="relative flex h-12 w-12 items-center justify-center rounded-full whatsapp-pulse"
                  style={{
                    background: 'radial-gradient(circle at 32% 26%, #8CFFC0 0%, #25D366 45%, #0E8A46 75%, #05502A 100%)',
                    boxShadow: '0 1px 0 rgba(255,255,255,0.6) inset, 0 12px 22px -10px rgba(0,0,0,0.9)',
                  }}
                >
                  <FaWhatsapp size={26} className="text-white" />
                </div>
              </div>

              <div className="min-w-0 flex-1">
                <p className="truncate font-display text-[0.82rem] font-semibold text-white">
                  Get your Gaming ID in 2 minutes
                </p>
                <p className="truncate text-[0.68rem] text-white/55">
                  Login · Sign up · App download · Instant payout
                </p>
              </div>

              <button onClick={() => setOpen(true)} className="btn-3d btn-3d-wa !px-4 !py-2.5 text-[0.78rem]">
                Chat now
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Keeps page content clear of the mobile bar */}
      <div className="h-20 md:hidden" aria-hidden="true" />
    </>
  );
};

export default WhatsAppFloat;
